import { z } from 'zod'
import { UUID_V7_PATTERN } from './uuid.ts'

/**
 * Branded identifier types, one per aggregate.
 *
 * Every identifier in this system is the same shape — a canonical UUIDv7 string — which
 * means the type system cannot tell a `RoomId` from a `TaskId` unless it is told. It is
 * told here. `getRoomTask(roomId, taskId)` called with its arguments swapped is a compile
 * error rather than an empty result set, and in a system whose central join table is
 * `(room_id, task_id)` that is worth the small ceremony.
 *
 * The brand is Zod's, so one object is simultaneously the runtime validator, the OpenAPI
 * schema, and the static type. `packages/db` re-attaches the same brands with Drizzle's
 * `$type<...>()`, so a repository returns branded ids without re-parsing anything.
 */

/**
 * A canonical UUIDv7, unbranded.
 *
 * The pattern rejects upper case, so the `pattern` published in the OpenAPI document is
 * exactly what the runtime enforces. That equality matters more than it looks: QA works
 * from the specification and nothing else, so a spec that advertises a laxer pattern than
 * the code accepts is a documented behaviour the system does not have.
 */
export const uuidV7Schema = z
  .string()
  .regex(UUID_V7_PATTERN, { error: 'Expected a canonical lowercase UUIDv7.' })
  .meta({ format: 'uuid' })

/** Builds one branded identifier schema. */
function entityId<Name extends string>(name: Name, description: string) {
  return uuidV7Schema.meta({ id: name, description }).brand<Name>()
}

// --- Tenancy ---------------------------------------------------------------------

export const tenantIdSchema = entityId('TenantId', 'Identifier of a tenant (a cleaning company).')
export type TenantId = z.infer<typeof tenantIdSchema>

export const userIdSchema = entityId('UserId', 'Identifier of a user inside a tenant.')
export type UserId = z.infer<typeof userIdSchema>

export const platformAdminIdSchema = entityId(
  'PlatformAdminId',
  'Identifier of a platform administrator, who belongs to no tenant.',
)
export type PlatformAdminId = z.infer<typeof platformAdminIdSchema>

export const accessRequestIdSchema = entityId(
  'AccessRequestId',
  'Identifier of a self-service access request awaiting an admin.',
)
export type AccessRequestId = z.infer<typeof accessRequestIdSchema>

// --- Authentication ---------------------------------------------------------------
//
// Added by F03 with the tables they identify. Sessions and refresh tokens are modelled
// once per population — tenant users and platform admins never share a row, because a
// platform admin has no tenant and `tenant_id` is `NOT NULL` everywhere it exists
// (`docs/DECISIONS.md`, "Platform-admin identity").
//
// `BuildIdentifierKeyId` (A02) identified a row in `build_identifier_keys`, the signing-key
// table dropped by the build-key-contract task — see `docs/DECISIONS.md`, "The build
// identifier is a plain rotatable key on the tenant row, not a signed, kid-rotated token".
// The build key itself lives on `tenants` and needs no identifier of its own: it is not a
// row a foreign key ever points to, it is a value looked up and rotated in place. See
// `./build-key.ts`.

export const sessionIdSchema = entityId(
  'SessionId',
  "Identifier of one sign-in of a tenant user, holding that sign-in's refresh-token chain.",
)
export type SessionId = z.infer<typeof sessionIdSchema>

export const refreshTokenIdSchema = entityId(
  'RefreshTokenId',
  'Identifier of one single-use refresh token inside a session.',
)
export type RefreshTokenId = z.infer<typeof refreshTokenIdSchema>

export const platformAdminSessionIdSchema = entityId(
  'PlatformAdminSessionId',
  'Identifier of one sign-in of a platform administrator.',
)
export type PlatformAdminSessionId = z.infer<typeof platformAdminSessionIdSchema>

export const platformAdminRefreshTokenIdSchema = entityId(
  'PlatformAdminRefreshTokenId',
  'Identifier of one single-use refresh token inside a platform-administrator session.',
)
export type PlatformAdminRefreshTokenId = z.infer<typeof platformAdminRefreshTokenIdSchema>

export const authTokenIdSchema = entityId(
  'AuthTokenId',
  'Identifier of a single-use invite or password-reset token.',
)
export type AuthTokenId = z.infer<typeof authTokenIdSchema>

export const rateLimitEventIdSchema = entityId(
  'RateLimitEventId',
  'Identifier of one accepted rate-limited call, recorded for the sliding window in ' +
    'middleware/rate-limit.ts.',
)
export type RateLimitEventId = z.infer<typeof rateLimitEventIdSchema>

/**
 * Identifier of a row in the retired `public_route_rate_limit_buckets` table.
 *
 * The table (and this id) has no reader or writer left in application code as of
 * `fix-branding-rate-limit-late-misses` — `RateLimitEventId` above replaced it. It stays
 * declared, physically present, and its migration undropped because
 * `docs/ENVIRONMENTS.md` §6 requires migrations to stay backward-compatible with the
 * currently-deployed Worker (expand first, contract later): the deployed code still
 * writes to this table until the release that adds the new one is live, so dropping it
 * in the same release would 500 every request to the one public route this mechanism
 * guards for the whole deploy window. `chore-drop-rate-limit-buckets` removes both once
 * that window has safely passed.
 */
export const rateLimitBucketIdSchema = entityId(
  'RateLimitBucketId',
  'Identifier of one fixed-window rate-limit counter row in the retired bucket table — ' +
    'see RateLimitEventId and chore-drop-rate-limit-buckets.',
)
export type RateLimitBucketId = z.infer<typeof rateLimitBucketIdSchema>

// --- Catalogue -------------------------------------------------------------------

export const locationIdSchema = entityId('LocationId', 'Identifier of a serviced location.')
export type LocationId = z.infer<typeof locationIdSchema>

export const roomIdSchema = entityId('RoomId', 'Identifier of a room within a location.')
export type RoomId = z.infer<typeof roomIdSchema>

export const taskIdSchema = entityId('TaskId', 'Identifier of a task in the catalogue.')
export type TaskId = z.infer<typeof taskIdSchema>

export const roomTaskIdSchema = entityId(
  'RoomTaskId',
  'Identifier of the join between a room and a task, which carries the recurrence cadence.',
)
export type RoomTaskId = z.infer<typeof roomTaskIdSchema>

export const hintIdSchema = entityId('HintId', 'Identifier of a hint attached to a task.')
export type HintId = z.infer<typeof hintIdSchema>

export const inventoryItemIdSchema = entityId(
  'InventoryItemId',
  'Identifier of a consumable inventory item.',
)
export type InventoryItemId = z.infer<typeof inventoryItemIdSchema>

// --- Work ------------------------------------------------------------------------

export const assignmentIdSchema = entityId('AssignmentId', 'Identifier of a daily assignment.')
export type AssignmentId = z.infer<typeof assignmentIdSchema>

export const assignmentGroupIdSchema = entityId(
  'AssignmentGroupId',
  'Identifier shared by every assignment created as one recurring series.',
)
export type AssignmentGroupId = z.infer<typeof assignmentGroupIdSchema>

export const assignmentTaskIdSchema = entityId(
  'AssignmentTaskId',
  'Identifier of a task materialised onto one assignment by the cadence engine.',
)
export type AssignmentTaskId = z.infer<typeof assignmentTaskIdSchema>

export const reportIdSchema = entityId('ReportId', 'Identifier of a completion report.')
export type ReportId = z.infer<typeof reportIdSchema>

export const reportRoomIdSchema = entityId(
  'ReportRoomId',
  'Identifier of one room outcome inside a report.',
)
export type ReportRoomId = z.infer<typeof reportRoomIdSchema>

export const mediaIdSchema = entityId(
  'MediaId',
  'Identifier of a stored media object (report, hint or inventory photo).',
)
export type MediaId = z.infer<typeof mediaIdSchema>

export const reportInventoryUsageIdSchema = entityId(
  'ReportInventoryUsageId',
  'Identifier of one "running low" mark recorded against an inventory item in a report.',
)
export type ReportInventoryUsageId = z.infer<typeof reportInventoryUsageIdSchema>

export const exportJobIdSchema = entityId('ExportJobId', 'Identifier of an export job.')
export type ExportJobId = z.infer<typeof exportJobIdSchema>

export const assignmentExpirySweepIdSchema = entityId(
  'AssignmentExpirySweepId',
  "Identifier of one tenant-local date the expiry sweep has processed — the ledger's row.",
)
export type AssignmentExpirySweepId = z.infer<typeof assignmentExpirySweepIdSchema>
